import React, { useEffect, useMemo, useState } from 'react';
import { CatalogFilters, ProductGrid } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  CatalogProduct,
  ProductStatus,
  SortOption,
  CatalogQuickFilter,
  sortProducts,
} from './catalogTypes';
import { useSkusStore } from '../../../store/skusStore';
import { useCartStore } from '../../../store/cartStore';
import { Sku } from '../../../services/skusApi';
import { getSuggestedReorder } from '../../../services/ordersApi';
import { listPromotions, Promotion } from '../../../services/promotionsApi';
import { getFavoriteSkuIds } from '../../../utils/favoritesStore';
import styles from './CustomerCatalog.module.css';

const NEW_WINDOW_DAYS = 30;

function deriveStatus(stock: number): ProductStatus {
  if (stock <= 0) return 'out-of-stock';
  if (stock < 25) return 'low-stock';
  return 'in-stock';
}

function skuHasActiveDeal(sku: Sku, promotions: Promotion[]): boolean {
  const now = Date.now();
  return promotions.some((p) => {
    if (!p.is_active) return false;
    if (new Date(p.starts_at).getTime() > now || new Date(p.ends_at).getTime() < now) return false;
    if (p.scope === 'all') return true;
    if (p.scope === 'category') return p.category_id != null && p.category_id === sku.category_id;
    if (p.scope === 'sku') return p.sku_id != null && p.sku_id === sku.id;
    return false;
  });
}

function adapt(
  s: Sku,
  previouslyOrderedSkuIds: Set<number>,
  promotions: Promotion[]
): CatalogProduct {
  // VP-CUST-002: real per-customer tier price, resolved server-side.
  // customer_price falls back to list_price when the caller isn't a
  // Customer or the SKU has no list_price at all.
  const effectivePrice = s.customer_price ?? s.list_price ?? s.unit_cost;
  const listPrice = s.list_price ?? s.unit_cost;
  const isNew = (Date.now() - new Date(s.created_at).getTime()) < NEW_WINDOW_DAYS * 86_400_000;

  return {
    id: String(s.id),
    name: s.product_name,
    sku: s.sku_code,
    packSize: 'Case',
    price: effectivePrice,
    originalPrice: listPrice !== effectivePrice ? listPrice : undefined,
    image: s.image_url ?? undefined,
    status: deriveStatus(s.total_stock),
    category: s.category_name || 'Other',
    isPreviouslyOrdered: previouslyOrderedSkuIds.has(s.id),
    isNew,
    isDeal: skuHasActiveDeal(s, promotions),
  };
}

const CustomerCatalog: React.FC = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All Categories');
  const [sort, setSort] = useState<SortOption>('popularity');
  const [quickFilter, setQuickFilter] = useState<CatalogQuickFilter>('all');
  const [toast, setToast] = useState<string | null>(null);
  const [previouslyOrderedSkuIds, setPreviouslyOrderedSkuIds] = useState<Set<number>>(new Set());
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [favoriteVersion, setFavoriteVersion] = useState(0);

  const skus = useSkusStore((s) => s.items);
  const loading = useSkusStore((s) => s.loading);
  const error = useSkusStore((s) => s.error);
  const setFilters = useSkusStore((s) => s.setFilters);
  const fetchList = useSkusStore((s) => s.fetchList);
  const addItem = useCartStore((s) => s.addItem);

  useEffect(() => {
    setFilters({ status: 'active' });
    fetchList();
    // "Previously Ordered" filter data — the customer's own order history.
    // Note: GET /customers/me/orders only returns an `item_count` per order,
    // not real line items (the same orders-list summary-shape gotcha the
    // Ecommerce API integration batch hit) — so this uses the
    // suggested-reorder endpoint instead, which is already backed by a real
    // per-SKU aggregation of the customer's order_items history.
    // customerId is ignored server-side for the Customer role (always
    // resolved to the caller), so any placeholder value works here.
    getSuggestedReorder('me')
      .then((lines) => setPreviouslyOrderedSkuIds(new Set(lines.map((l) => l.sku_id))))
      .catch(() => setPreviouslyOrderedSkuIds(new Set()));
    // "Deals" filter data — active promotions, matched by scope.
    listPromotions().then(setPromotions).catch(() => setPromotions([]));
  }, [setFilters, fetchList]);

  const adapted = useMemo<CatalogProduct[]>(
    () => skus.map((s) => adapt(s, previouslyOrderedSkuIds, promotions)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skus, previouslyOrderedSkuIds, promotions, favoriteVersion]
  );

  const handleAddToCart = (productId: string, quantity: number) => {
    const sku = skus.find((s) => String(s.id) === productId);
    if (!sku) return;
    addItem(
      {
        sku_id: sku.id,
        sku_code: sku.sku_code,
        name: sku.product_name,
        unit_price: sku.customer_price ?? sku.list_price ?? sku.unit_cost,
        image_url: sku.image_url,
      },
      quantity
    );
    setToast(`Added ${quantity} × ${sku.product_name}`);
    window.setTimeout(() => setToast(null), 1600);
  };

  const filtered = useMemo(() => {
    const list = adapted.filter((p) => {
      const matchesSearch =
        !search ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.sku.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = category === 'All Categories' || p.category === category;
      let matchesQuickFilter = true;
      switch (quickFilter) {
        case 'favorites':
          matchesQuickFilter = getFavoriteSkuIds().has(p.id);
          break;
        case 'previously-ordered':
          matchesQuickFilter = p.isPreviouslyOrdered;
          break;
        case 'deals':
          matchesQuickFilter = p.isDeal;
          break;
        case 'new':
          matchesQuickFilter = p.isNew;
          break;
        default:
          matchesQuickFilter = true;
      }
      return matchesSearch && matchesCategory && matchesQuickFilter;
    });
    return sortProducts(list, sort);
    // favoriteVersion forces a re-filter after a favorite toggle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapted, search, category, sort, quickFilter, favoriteVersion]);

  return (
    <div className={styles.container}>
      <CatalogFilters
        search={search}
        onSearchChange={setSearch}
        category={category}
        onCategoryChange={setCategory}
        sort={sort}
        onSortChange={setSort}
        quickFilter={quickFilter}
        onQuickFilterChange={setQuickFilter}
      />
      <AsyncBoundary
        loading={loading && skus.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No products in the catalog yet"
        onRetry={() => fetchList()}
      >
        <ProductGrid
          products={filtered}
          onAddToCart={handleAddToCart}
          onFavoriteChange={() => setFavoriteVersion((v) => v + 1)}
        />
      </AsyncBoundary>

      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
};

export const CustomerCatalogPage: React.FC = () => <CustomerCatalog />;

export { CustomerCatalog };
export default CustomerCatalog;
